// src/components/Header/Header.js
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { clearSessionUser } from "../../auth/sessionAuth";
import api from "../../services/api";
import jalaali from "jalaali-js"; 

export default function Header() {
  const navigate = useNavigate();
  const root = document.documentElement;
  
  // --- Profile & General State ---
  const [profile, setProfile] = useState(null);
  const [totalUnread, setTotalUnread] = useState(0); 

  // --- Modal State ---
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [editingField, setEditingField] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // --- اضافه شده: استیت برای پیش‌نمایش عکس ---
  const [previewUrl, setPreviewUrl] = useState(null);

  // --- Date Specific State ---
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear, setBirthYear] = useState("");

  const notifySocketRef = useRef(null);

  // Fetch Profile
  const fetchProfile = async () => {
    try {
      const res = await api.get("profile/");
      if (res.data && res.data.length > 0) {
        setProfile(res.data[0]);
      }
    } catch (error) {
      console.error("Error fetching profile", error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
        const res = await api.get("chat/unread-count/");
        setTotalUnread(res.data.total_unread);
    } catch (error) {
        console.error("Error fetching unread count", error);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchUnreadCount();
  }, []);

  // --- WebSocket ---
  useEffect(() => {
    if (!profile) return;
    const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
    const host = '127.0.0.1:8000'; 
    const notifyUrl = `${protocol}${host}/ws/notify/`;
    const notifyWs = new WebSocket(notifyUrl);
    notifySocketRef.current = notifyWs;
    notifyWs.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.message_type === 'new_message') setTotalUnread(prev => prev + 1);
    };
    return () => { if (notifySocketRef.current) notifySocketRef.current.close(); };
  }, [profile]);

  // --- اضافه شده: مدیریت پیش‌نمایش عکس ---
  useEffect(() => {
    if (selectedFile) {
        // اگر فایل جدید انتخاب شده، یک آدرس موقت برایش بساز
        const objectUrl = URL.createObjectURL(selectedFile);
        setPreviewUrl(objectUrl);
        
        // وقتی کامپوننت بسته شد یا فایل عوض شد، حافظه را آزاد کن
        return () => URL.revokeObjectURL(objectUrl);
    } else {
        // اگر فایلی انتخاب نشده، عکس فعلی پروفایل را نشان بده (یا هیچی)
        setPreviewUrl(profile?.avatar || null);
    }
  }, [selectedFile, profile]);


  // --- Open Modal Logic ---
  const openEditModal = (field, title, currentValue = "") => {
    setEditingField(field);
    setModalTitle(title);
    setSelectedFile(null); // فایل قبلی را پاک کن
    setPreviewUrl(profile?.avatar || null); // پیش‌نمایش را ریست کن به عکس پروفایل
    setShowModal(true);

    if (field === "birth_date" && currentValue) {
      try {
        const datePart = currentValue.split("T")[0]; 
        const [gYear, gMonth, gDay] = datePart.split("-").map(Number);
        const jDate = jalaali.toJalaali(gYear, gMonth, gDay);
        setBirthYear(jDate.jy); setBirthMonth(jDate.jm); setBirthDay(jDate.jd);
      } catch (e) { setBirthYear(""); setBirthMonth(""); setBirthDay(""); }
    } else {
      setInputValue(currentValue || "");
      setBirthYear(""); setBirthMonth(""); setBirthDay("");
    }
  };

  // --- Handle Delete Avatar ---
  const handleDeleteAvatar = async () => {
    if (selectedFile) {
        setSelectedFile(null); // فقط فایل انتخابی را پاک کن
        return;
    }
    if (profile?.avatar) {
        if (!window.confirm("آیا مطمئن هستید که می‌خواهید تصویر پروفایل را حذف کنید؟")) return;
        setIsSubmitting(true);
        try {
            await api.patch(`profile/${profile.id}/`, { avatar: null });
            await fetchProfile();
            setShowModal(false);
        } catch (error) {
            console.error("Error deleting avatar", error);
            alert("خطا در حذف تصویر.");
        } finally {
            setIsSubmitting(false);
        }
    }
  };

  // --- Save Changes Logic ---
  const handleSaveChanges = async (e) => {
    e.preventDefault();
    if (!profile?.id) return;
    setIsSubmitting(true);
    try {
      const url = `profile/${profile.id}/`;
      let payload;
      let config = {};

      if (editingField === "avatar") {
        if (!selectedFile) { alert("لطفا یک تصویر انتخاب کنید"); setIsSubmitting(false); return; }
        payload = new FormData();
        payload.append("avatar", selectedFile);
        config = { headers: { "Content-Type": "multipart/form-data" } };
      } 
      else if (editingField === "birth_date") {
        if (!birthYear || !birthMonth || !birthDay) { alert("لطفا تاریخ را کامل وارد کنید"); setIsSubmitting(false); return; }
        try {
            const gDate = jalaali.toGregorian(Number(birthYear), Number(birthMonth), Number(birthDay));
            const formattedDate = `${gDate.gy}-${String(gDate.gm).padStart(2, '0')}-${String(gDate.gd).padStart(2, '0')}`;
            payload = { birth_date: formattedDate };
        } catch (error) { alert("تاریخ وارد شده معتبر نیست"); setIsSubmitting(false); return; }
      } 
      else { payload = { [editingField]: inputValue }; }

      await api.patch(url, payload, config);
      await fetchProfile();
      setShowModal(false);
    } catch (error) {
      console.error("Error updating", error);
      alert("خطا در ذخیره اطلاعات.");
    } finally { setIsSubmitting(false); }
  };

  // --- Menu Control ---
  const openCls = (cls) => root.classList.add(cls);
  const closeCls = (cls) => root.classList.remove(cls);
  const closeAll = () => { closeCls("open-menu"); closeCls("open-user-action"); closeCls("open-add-to-card"); closeCls("open-add-to-call"); };
  const openMenu = (e) => { e?.preventDefault?.(); openCls("open-menu"); };
  const closeMenu = (e) => { e?.preventDefault?.(); closeCls("open-menu"); };
  const openUser = (e) => { e?.preventDefault?.(); openCls("open-user-action"); };
  const closeUser = (e) => { e?.preventDefault?.(); closeCls("open-user-action"); };

  const handleLogout = (e) => {
    e?.preventDefault?.(); closeAll(); clearSessionUser();
    localStorage.removeItem("token"); navigate("/login", { replace: true });
  };

  // --- Theme Logic ---
  const STORAGE_KEY = "theme";
  const DARK_CLASS = "light-mode"; 
  const isDark = () => root.classList.contains(DARK_CLASS);
  const [themeLabel, setThemeLabel] = useState("حالت روشن");
  const updateThemeUI = () => setThemeLabel(isDark() ? "حالت شب" : "حالت روشن");
  const applyTheme = (theme, persist = false) => {
    root.classList.toggle(DARK_CLASS, theme === "dark");
    if (persist) try { localStorage.setItem(STORAGE_KEY, theme); } catch {}
    updateThemeUI();
  };
  const toggleTheme = () => applyTheme(isDark() ? "light" : "dark", true);
  
  useEffect(() => {
    let initial = localStorage.getItem(STORAGE_KEY);
    if (initial === "dark" || initial === "light") applyTheme(initial, false);
    else { const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches; applyTheme(prefersDark ? "dark" : "light", false); }
    const onDocClick = (e) => {
      const t = e.target;
      if (t.closest("#addToCard")) { e.preventDefault(); openCls("open-add-to-card"); }
      if (t.closest(".add-to-card-close")) { e.preventDefault(); closeCls("open-add-to-card"); }
      if (t.closest("#addToCall")) { e.preventDefault(); openCls("open-add-to-call"); }
      if (t.closest(".add-to-call-close")) { e.preventDefault(); closeCls("open-add-to-call"); }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const avatarSrc = profile?.avatar || "/img/profile.webp";
  const fullName = profile?.user_info?.full_name || "کاربر ناشناس";
  const roleName = profile?.level_title || "کارمند عادی";
  const getJalaliDateString = (gDateString) => {
      if (!gDateString) return "---";
      try {
          const datePart = gDateString.split("T")[0];
          const [gYear, gMonth, gDay] = datePart.split("-").map(Number);
          const jDate = jalaali.toJalaali(gYear, gMonth, gDay);
          return `${jDate.jy}/${jDate.jm}/${jDate.jd}`;
      } catch { return gDateString; }
  };

  return (
    <>
      <Link to="/chatroom" className="chatroom-link" onClick={closeAll}>
        {totalUnread > 0 && <span>{totalUnread}</span>}
        <svg><use xlinkHref="#msgIcon2" /></svg>
      </Link>

      <header className="primary-header">
        <section className="header-menu-desctop">
          <button id="menuBtn" onClick={openMenu} type="button">
            <svg><use xlinkHref="#svg_menu" /></svg>
          </button>

          <Link to="/" className="logo-box" onClick={() => closeAll()}>
            <img src="/img/logo.png" alt="logo" width="150" height="32" />
          </Link>

          <section className="user-action">
            <button id="btnUser" type="button" onClick={openUser}>
              {profile?.avatar ? ( <img src={profile.avatar} alt="profile" /> ) : ( <svg><use xlinkHref="#profileIcon" /></svg> )}
            </button>
          </section>

          <div className="user-box">
            <section>
              <h2>حساب کاربری</h2>
              <button className="user-close" type="button" onClick={closeUser}>
                <svg><use xlinkHref="#arrowLeftIcon" /></svg>
              </button>
            </section>
            <ul>
              <li>
                <button type="button" onClick={() => openEditModal("avatar", "آپلود آواتار")}>
                  آپلود آواتار<span>تصویر جدید</span>
                </button>
              </li>
              <p>اطلاعات هویتی</p>
              <li><button type="button" onClick={() => openEditModal("phone", "شماره تماس", profile?.phone)}>شماره تماس<span>{profile?.phone || "---"}</span></button></li>
              <li><button type="button" onClick={() => openEditModal("national_code", "کد ملی", profile?.national_code)}>کد ملی<span>{profile?.national_code || "---"}</span></button></li>
              <li><button type="button" onClick={() => openEditModal("birth_date", "تاریخ تولد", profile?.birth_date)}>تاریخ تولد<span>{getJalaliDateString(profile?.birth_date)}</span></button></li>
              <p>اطلاعات بانکی</p>
              <li><button type="button" onClick={() => openEditModal("card_number", "شماره کارت", profile?.card_number)}>شماره کارت<span>{profile?.card_number || "---"}</span></button></li>
              <li><button type="button" onClick={() => openEditModal("sheba_number", "شماره شبا", profile?.sheba_number)}>شماره شبا<span>{profile?.sheba_number || "---"}</span></button></li>
              <p>وضعیت فعلی</p>
              <li><button type="button" onClick={() => openEditModal("daily_status", "وضعیت روزانه", profile?.daily_status)}>وضعیت روزانه<span>{profile?.daily_status || "ثبت نشده"}</span></button></li>
            </ul>
            <a href="/login" className="log-out" onClick={handleLogout}>خروج <svg><use xlinkHref="#signInIcon" /></svg></a>
          </div>
        </section>

        <nav className="list-wrapper">
          <div className="name-wrapper">
            <img src={avatarSrc} alt="profile" width="56" height="56" style={{borderRadius: '50%', objectFit: 'cover'}} />
            <section>
              <h2>{fullName}</h2>
              <h3 style={{ fontSize: '0.8rem', opacity: 0.8 }}>{roleName}</h3>
            </section>
          </div>
          <ul>
            <li><div id="btnUserMenu" role="button" tabIndex={0} onClick={(e) => { e.preventDefault(); closeMenu(); openUser(); }}><span><svg><use xlinkHref="#profileIcon" /></svg>حساب کاربری</span></div></li>
            <li><Link to="/" onClick={() => closeAll()}><span><svg><use xlinkHref="#homeIcon" /></svg>داشبورد</span></Link></li>
            <li><Link to="/reports" onClick={() => closeAll()}><span><svg><use xlinkHref="#bookIcon" /></svg>گزارش‌ها</span></Link></li>
          </ul>
          <button id="themeToggle" aria-label={themeLabel} type="button" onClick={toggleTheme}>
            <span><svg aria-label="lightTheme"><use xlinkHref="#lightIcon" /></svg>تغییر پوسته</span>
            <div><bdi></bdi></div>
          </button>
        </nav>
        <div className="overlay-menu" onClick={closeAll}></div>
      </header>

      {/* --- Edit Modal --- */}
      {showModal && (
        <div className="edit-modal-overlay">
          <div className="edit-modal-content">
            <div className="edit-modal-header">
              <h3>ویرایش {modalTitle}</h3>
              <button onClick={() => setShowModal(false)} className="close-modal-btn">&times;</button>
            </div>
            
            <form onSubmit={handleSaveChanges}>
              <div className="edit-modal-body">
                
                {/* 1. Avatar Input (با پیش‌نمایش داخل کادر) */}
                {editingField === "avatar" ? (
                  <div className="file-input-wrapper">
                    {/* استایل‌دهی اینلاین برای اینکه عکس کل کادر را بگیرد */}
                    <label 
                        htmlFor="file-upload" 
                        className="custom-file-upload"
                    >
                        {previewUrl ? (
                            <img 
                                src={previewUrl} 
                                alt="preview"
                            />
                        ) : (
                            "انتخاب تصویر..."
                        )}
                        
                    </label>
                    
                    {(selectedFile || profile?.avatar) && (
                        <button 
                            type="button" 
                            onClick={handleDeleteAvatar}
                            className="del-btn"
                        >
                            {selectedFile ? "🗑️" : "🗑️"}
                        </button>
                    )}
                    
                    <input 
                      id="file-upload" 
                      type="file" 
                      accept="image/*"
                      onChange={(e) => setSelectedFile(e.target.files[0])} 
                    />
                    
                  </div>
                ) 
                
                // 2. Date Input (3 Fields)
                : editingField === "birth_date" ? (
                    <div className="date-inputs-container">
                        <div className="date-field">
                            <label>روز</label>
                            <input type="number" min="1" max="31" placeholder="01" value={birthDay} onChange={(e) => setBirthDay(e.target.value)} className="modal-input date-input" />
                        </div>
                        <div className="date-field">
                            <label>ماه</label>
                            <input type="number" min="1" max="12" placeholder="01" value={birthMonth} onChange={(e) => setBirthMonth(e.target.value)} className="modal-input date-input" />
                        </div>
                        <div className="date-field">
                            <label>سال</label>
                            <input type="number" min="1300" max="1450" placeholder="1370" value={birthYear} onChange={(e) => setBirthYear(e.target.value)} className="modal-input date-input" />
                        </div>
                    </div>
                )
                
                // 3. Textarea (Status)
                : editingField === "daily_status" ? (
                  <textarea rows="4" value={inputValue} onChange={(e) => setInputValue(e.target.value)} placeholder="توضیحات وضعیت خود را بنویسید..." className="modal-input" />
                ) 
                
                // 4. Default Input
                : (
                  <input type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} placeholder={`مقدار ${modalTitle} را وارد کنید`} className="modal-input" />
                )}
              </div>
              
              <div className="edit-modal-footer">
                <button type="submit" className="save-btn" disabled={isSubmitting}>
                  {isSubmitting ? "در حال ذخیره..." : "ثبت تغییرات"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}